import { useEffect, useState } from 'react';

/**
 * Breakpoints para diseño responsive
 */
export const Breakpoints = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
  wide: 1440,
};

/**
 * Tipos de dispositivo según breakpoint
 */
export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'wide';

/**
 * Hook para detectar el tipo de dispositivo y tamaño de pantalla
 * Retorna información sobre el breakpoint actual y utilidades para diseño responsive
 */
export function useResponsive() {
  const [dimensions, setDimensions] = useState(() => {
    if (typeof window !== 'undefined') {
      return { width: window.innerWidth, height: window.innerHeight };
    }
    return { width: 1024, height: 768 };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { width, height } = dimensions;

  // Determinar tipo de dispositivo basado en el ancho
  const getDeviceType = (): DeviceType => {
    if (width >= Breakpoints.wide) return 'wide';
    if (width >= Breakpoints.desktop) return 'desktop';
    if (width >= Breakpoints.tablet) return 'tablet';
    return 'mobile';
  };

  const deviceType = getDeviceType();

  // Helpers para queries de breakpoint
  const isMobile = deviceType === 'mobile';
  const isTablet = deviceType === 'tablet';
  const isDesktop = deviceType === 'desktop' || deviceType === 'wide';
  const isWide = deviceType === 'wide';

  // Determinar si es web (siempre true en esta versión)
  const isWeb = true;

  // Ancho máximo para contenido en escritorio
  const maxContentWidth = isDesktop ? 1200 : undefined;

  // Padding horizontal responsive
  const getHorizontalPadding = () => {
    if (isDesktop) return 40;
    if (isTablet) return 32;
    return 24;
  };

  // Número de columnas para grids
  const getGridColumns = (baseColumns: number = 1) => {
    if (isDesktop) return Math.min(baseColumns * 2, 4);
    if (isTablet) return Math.min(baseColumns * 1.5, 3);
    return baseColumns;
  };

  return {
    width,
    height,
    deviceType,
    isMobile,
    isTablet,
    isDesktop,
    isWide,
    isWeb,
    maxContentWidth,
    getHorizontalPadding,
    getGridColumns,
    breakpoints: Breakpoints,
  };
}
